import GuideLayout from "./GuideLayout";
import { TransparencyFigure, VectorVsRasterFigure, ToolWalkthroughFigure } from "./GuideFigures";

export default function ConvertEpsToPng() {
  return (
    <GuideLayout
      title="How to Convert EPS to PNG (Free, Online)"
      description="Convert EPS to PNG online for free. Step-by-step guide covering resolution, transparency, quality tips, troubleshooting blurry output, and when to use PNG vs JPG or PDF."
      path="/guides/convert-eps-to-png"
      updated="June 30, 2026"
      related={["/guides/convert-eps-to-jpg", "/guides/convert-eps-to-pdf", "/guides/vector-vs-raster"]}
    >
      <p className="lead">
        EPS is a vector format most apps and websites can&apos;t display. Converting
        it to PNG turns it into a universal image you can drop into a document,
        slide, email or web page. Here&apos;s how to do it free in your browser —
        and how to get the best possible quality.
      </p>

      <h2>Convert EPS to PNG — step by step</h2>
      <p>
        Open the <a href="/">EPS Viewer</a> and the whole conversion takes three steps.
        Here is exactly what you will see at each one:
      </p>
      <ToolWalkthroughFigure intro="The three stages of a conversion in EPS Viewer, shown as they appear in the browser. No installation, no account, and it works on every operating system — Windows, Mac, Linux and mobile." />
      <p>
        If the preview looks right, click <strong>Download PNG</strong>. If it does not,
        skip to <a href="#troubleshooting-blurry">troubleshooting</a> below — a blurry or
        empty preview usually points at something specific in the source file.
      </p>

      <h2>Why convert EPS to PNG?</h2>
      <ul>
        <li><strong>Universal compatibility:</strong> PNG opens everywhere — Word, Google Docs, PowerPoint, social media, Slack, email — without needing design software.</li>
        <li><strong>Transparency support:</strong> PNG supports transparent backgrounds, which is essential for logos placed on coloured slides or web pages.</li>
        <li><strong>No software needed for the recipient:</strong> send a PNG and anyone can view it instantly, without Illustrator or Inkscape.</li>
        <li><strong>Web-ready:</strong> browsers display PNG natively, unlike EPS which must be converted before it can appear on a website.</li>
      </ul>

      <h2>Understanding resolution: DPI and why it matters</h2>
      <p>
        When converting a vector EPS to a raster PNG, you choose a resolution measured in DPI
        (dots per inch). This determines how large and sharp the PNG will be.
      </p>
      <div className="table-wrap">
        <table className="tbl">
          <thead>
            <tr><th>Use case</th><th>Recommended DPI</th><th>Why</th></tr>
          </thead>
          <tbody>
            <tr><td>Website / social media</td><td>72–96 DPI</td><td>Screens are 72–96 DPI; higher won&apos;t look sharper but makes the file bigger</td></tr>
            <tr><td>Email signature / presentation</td><td>150 DPI</td><td>Good balance of sharpness and file size</td></tr>
            <tr><td>Print (brochures, flyers)</td><td>300 DPI</td><td>Standard for professional print; anything below looks soft when printed</td></tr>
            <tr><td>Large format print (banners)</td><td>150 DPI at final size</td><td>Large prints are viewed from a distance, so 150 DPI is usually sufficient</td></tr>
          </tbody>
        </table>
      </div>
      <p>
        The EPS Viewer converts at a high-quality resolution suitable for most screen uses.
        For print work requiring 300 DPI, use Inkscape (free) or Adobe Illustrator and export
        at the exact size and DPI you need.
      </p>

      <h2>What happens to transparency when converting?</h2>
      <p>
        PNG fully supports transparent backgrounds. However, whether your converted PNG actually has
        a transparent background depends on the original EPS:
      </p>
      <ul>
        <li>If the EPS artwork has a white background baked in, the PNG will have a white background.</li>
        <li>If the EPS artwork sits on no background (true vector with no fill), the PNG will have a transparent background.</li>
        <li>EPS does not support true transparency — drop shadows and semi-transparent overlays are &quot;flattened&quot; on export. If your artwork relies on transparency effects, consider converting to PDF instead, which handles transparency natively.</li>
      </ul>
      <TransparencyFigure caption="PNG carries an alpha channel, so a transparent background survives the export exactly as it was in the source artwork. This is the main reason to choose PNG over JPG for logos and any graphic that has to sit on a coloured background — JPG has no alpha channel and fills the transparent area with solid white instead." />

      <h2>Vector vs raster: what changes when you convert</h2>
      <VectorVsRasterFigure />
      <p>
        EPS is <strong>vector</strong> — the artwork is described as mathematical paths (lines, curves, fills)
        that can be rendered at any size. PNG is <strong>raster</strong> — a fixed grid of pixels baked
        at a specific size and resolution.
      </p>
      <p>
        When you convert EPS to PNG, the vector artwork is <em>rasterized</em>: rendered into pixels
        at a chosen resolution. After that, enlarging the PNG will make it blurry because you&apos;re
        stretching a finite set of pixels. Always keep the original EPS if you might need the artwork
        at a larger size later.
      </p>
      <p>Read more in our guide on <a href="/guides/vector-vs-raster">vector vs raster graphics</a>.</p>

      <h2 id="troubleshooting-blurry">Troubleshooting: why does my PNG look blurry?</h2>
      <p>There are a few common reasons:</p>
      <ul>
        <li><strong>The PNG was exported too small:</strong> if you need to use a 2000px-wide image but exported at 500px, enlarging it causes blurring. Re-export at a larger size from Inkscape or Illustrator.</li>
        <li><strong>The EPS itself contains a low-resolution embedded preview:</strong> some EPS files include a low-res TIFF thumbnail. Open it in Inkscape to confirm the actual vector data is sharp.</li>
        <li><strong>You&apos;re zooming past 100%:</strong> all raster images look soft if you zoom in past their native resolution. This is normal — it doesn&apos;t mean the image is low quality at its intended display size.</li>
        <li><strong>The original EPS contained embedded raster images:</strong> EPS files can embed photos or pixel-based graphics. If the original raster content was low-resolution, the PNG will reflect that.</li>
      </ul>

      <h2>PNG vs JPG vs PDF: which format should you use?</h2>
      <div className="table-wrap">
        <table className="tbl">
          <thead>
            <tr><th>Format</th><th>Transparency</th><th>File size</th><th>Best for</th></tr>
          </thead>
          <tbody>
            <tr><td>PNG</td><td>Yes</td><td>Medium–large</td><td>Logos, icons, anything needing a transparent background</td></tr>
            <tr><td>JPG</td><td>No</td><td>Small</td><td>Photographs, web images with solid backgrounds</td></tr>
            <tr><td>PDF</td><td>Yes (native)</td><td>Variable</td><td>Print delivery, sharing with printers, multi-page documents</td></tr>
          </tbody>
        </table>
      </div>

      <h2>Tips for the best quality</h2>
      <ul>
        <li>Export at the largest size you&apos;ll need — you can always scale down later, but scaling up reduces quality.</li>
        <li>Keep the original EPS for future edits or higher-resolution exports.</li>
        <li>Use PNG for logos and artwork with transparency; use <a href="/guides/convert-eps-to-jpg">JPG</a> for photographs or artwork with solid backgrounds.</li>
        <li>For professional printing, always deliver the vector original (EPS or <a href="/guides/convert-eps-to-pdf">PDF</a>) rather than a rasterized PNG.</li>
        <li>If the PNG will appear on a website, compress it with a tool like TinyPNG to reduce load time without visible quality loss.</li>
      </ul>

      <h2>Frequently asked questions</h2>
      <dl className="guide-faq">
        <dt>Is converting EPS to PNG free?</dt>
        <dd>Yes. Our viewer lets you preview and download a PNG at no cost, with no signup or watermark.</dd>
        <dt>Will the PNG have a transparent background?</dt>
        <dd>It depends on the original EPS. PNG supports transparency, but if the EPS has a white background baked in, the PNG will too.</dd>
        <dt>Why does my PNG look blurry when I zoom in?</dt>
        <dd>PNG is pixel-based, so zooming past the image&apos;s native resolution softens it. Export at a larger size from Inkscape or Illustrator if you need a higher-resolution file.</dd>
        <dt>What DPI should I use for print?</dt>
        <dd>300 DPI is the standard for professional print work such as flyers, brochures and business cards. For screen use (web, presentations), 72–96 DPI is sufficient.</dd>
        <dt>Can I convert multiple EPS files at once?</dt>
        <dd>The online viewer processes one file at a time. For batch conversion, Inkscape supports command-line batch export on Windows, Mac and Linux.</dd>
      </dl>
    </GuideLayout>
  );
}
